import { ChevronRight, Plus } from 'lucide-react';
import kpLogo from '@/assets/kp-logo.png';
import { use, useState } from 'react';
import { Link, NavLink, useLocation, useResolvedPath } from 'react-router';
import type { OfficerIdentityDto } from '@pams/shared';
import { OfficerIdentity } from '@/components/OfficerIdentity';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { listOriginOf } from '@/lib/listOrigin';
import { ARRANGEMENTS_NAV, type NavGroup, type NavLeaf, type NavSection } from '@/lib/navigation';
import { SidebarNavigateContext } from '@/lib/sidebarContext';
import { cn } from '@/lib/utils';

/**
 * THE SIDEBAR.
 *
 * `sections` is a prop rather than an import so a test can inject a nested group and a
 * section action without inventing routes that 404 — the group and `+` machinery below is
 * built and tested but not yet populated, because a nav row that leads nowhere is worse
 * than an unused branch.
 *
 * ⚠️ THREE THINGS HERE ARE LOAD-BEARING FOR `AppShell.test.tsx`:
 *   1. Every leaf is a link (`role="link"`) with the label as its accessible name.
 *      [amended TYPED-BACK] A plain `Link` now, in EVERY state, with `NavLink`'s active rule
 *      computed here (`navLinkActive`) — `NavItem` says why it cannot be a `NavLink`.
 *   2. The label is `sr-only` in the rail, NEVER removed — an accessible name must not
 *      depend on the collapse state, and the P1a suite queries by name.
 *   3. `OfficerIdentity` is rendered here, always. See that file for why.
 */
export type AppSidebarProps = {
  sections: readonly NavSection[];
  officer: OfficerIdentityDto | undefined;
  collapsed?: boolean;
  /** The desktop nav is `Main`; the mobile drawer's copy must NOT also be. */
  navLabel?: string;
  onNavigate?: () => void;
};

/**
 * ─────────────────────────────────────────────────────────────────────────────────────
 *  ⚠️ [TYPED-BACK] ON AN ORDER OPENED FROM A TYPED LIST, THAT LIST'S ROW IS THE ACTIVE ONE.
 *
 *  `NavLink`'s rule is a PREFIX match, so every `/arrangements/…` page lit Manage Arrangements — a typed
 *  order ("Durga Puja PA") included, though that list did not contain it. ⚠️ [amended TYPE-FILTER] It does
 *  now (its "Order type" defaults to All), so a typed order opened with no `from` is rightly under Manage
 *  Arrangements, and its back link agrees. When an order page's URL
 *  carries a valid `?from=` (`listOriginOf`, which accepts a registered typed list and nothing else)
 *  the origin's row is active and Manage Arrangements is not. `true`/`false` is that decision; `null`
 *  — every other row, every other page, and any `from` that is not a typed list — leaves `NavLink`'s
 *  own rule standing (`navLinkActive`, held to a real `NavLink` by the tests).
 *
 *  ⚠️ ONLY ON AN ORDER PAGE. A `from` on any other page means nothing to the sidebar, and the typed
 *  list itself is already lit by `NavLink`'s own rule.
 * ─────────────────────────────────────────────────────────────────────────────────────
 */
const originActive = (to: string, location: { pathname: string; search: string }): boolean | null => {
  if (!location.pathname.startsWith(`${ARRANGEMENTS_NAV.to}/`)) return null;
  const origin = listOriginOf(location.search);
  if (origin === null) return null;
  if (to === origin.pathname) return true;
  if (to === ARRANGEMENTS_NAV.to) return false;
  return null;
};

const itemClass = (isActive: boolean, collapsed: boolean): string =>
  cn(
    'flex items-center gap-2 rounded-md px-2 py-1.5 text-sm font-medium transition-colors',
    collapsed && 'justify-center px-0',
    isActive
      ? // The active state is a FILLED NEUTRAL PILL — not a left accent bar, not a
        // coloured background. Brighter label and icon carry the emphasis.
        'bg-active text-foreground'
      : 'text-foreground/70 hover:bg-hover hover:text-foreground',
  );

/**
 * ─────────────────────────────────────────────────────────────────────────────────────
 *  `NavLink`'s OWN ACTIVE RULE, TRANSCRIBED FROM react-router 8.3.0 (`dist/…/lib/dom/lib.js`).
 *
 *  The row is a plain `Link` (below), so it computes what `NavLink` would have: the location's path
 *  equals the row's, or — without `end` — starts with it at a segment boundary; case-insensitively,
 *  `NavLink`'s default. A trailing slash on the row's own path is allowed for, and `/` alone matches
 *  only itself.
 *
 *  ⚠️ NOT `matchPath`/`useMatch`, WHICH ANSWERS DIFFERENTLY IN TWO PLACES: a pattern of `/` without
 *  `end` matches EVERY path, and `end` admits a trailing slash (`/x/`), where `NavLink` matches
 *  neither. `AppSidebar.test.tsx` holds each row to a reference `NavLink` at the same address, those
 *  two cases included, so a react-router upgrade that moves the rule fails there rather than here.
 *
 *  ⚠️ IT OMITS `navigator.encodeLocation`, which only changes a path holding characters a URL must
 *  escape — no row's `to` does — and `isPending`, which `itemClass` has never read.
 * ─────────────────────────────────────────────────────────────────────────────────────
 */
const navLinkActive = (toPathname: string, locationPathname: string, end: boolean): boolean => {
  const to = toPathname.toLowerCase();
  const at = locationPathname.toLowerCase();
  const endSlashPosition = to !== '/' && to.endsWith('/') ? to.length - 1 : to.length;
  return at === to || (!end && at.startsWith(to) && at.charAt(endSlashPosition) === '/');
};

const NavItem = ({ entry, collapsed = false }: { entry: NavLeaf; collapsed?: boolean }) => {
  const onNavigate = use(SidebarNavigateContext);
  const location = useLocation();
  const { pathname: toPathname } = useResolvedPath(entry.to);
  const Icon = entry.icon;

  /*
   * ⚠️ [TYPED-BACK] ONE ELEMENT TYPE FOR EVERY ROW IN EVERY STATE — A `Link` — AND ONE ACTIVE VALUE.
   *
   * The override cannot ride on a `NavLink`: it computes `aria-current` from its OWN `isActive`
   * (react-router 8.3.0: `isActive ? ariaCurrentProp : undefined`), so a prop can change the value it
   * uses but never switch it on or off. The first cut therefore rendered a `Link` for an overridden
   * row and a `NavLink` for the rest — two component types at one position, which React reads as two
   * nodes: crossing between an order page carrying `?from=` and any other page REMOUNTED the anchor
   * and dropped keyboard focus to `<body>`. Now `NavLink`'s rule is computed above, the origin
   * override (`originActive`) decides over it where it speaks, and the class and `aria-current` both
   * follow the one final value — the pill and the announcement cannot tell two stories.
   */
  const isActive = originActive(entry.to, location) ?? navLinkActive(toPathname, location.pathname, entry.end ?? false);

  return (
    <Link
      to={entry.to}
      onClick={onNavigate}
      title={collapsed ? entry.label : undefined}
      aria-current={isActive ? 'page' : undefined}
      className={itemClass(isActive, collapsed)}
    >
      <Icon className="size-4 shrink-0" aria-hidden />
      <span className={cn('truncate', collapsed && 'sr-only')}>{entry.label}</span>
    </Link>
  );
};

const NavGroupRow = ({ group, collapsed }: { group: NavGroup; collapsed: boolean }) => {
  const location = useLocation();
  /* ⚠️ [TYPED-BACK] A group opens on the row that will be LIT, so the origin override is asked first. */
  const [open, setOpen] = useState(() =>
    group.children.some((child) => originActive(child.to, location) ?? location.pathname.startsWith(child.to)),
  );
  const Icon = group.icon;

  /*
   * IN THE RAIL, A GROUP FLATTENS to its children as individual icon rows. A nested
   * disclosure inside 56px has nowhere to put the indent, and a fly-out would be a second
   * popup layer for no gain.
   */
  if (collapsed) {
    return (
      <>
        {group.children.map((child) => (
          <NavItem key={child.to} entry={child} collapsed />
        ))}
      </>
    );
  }

  const contentId = `nav-group-${group.id}`;

  return (
    <div>
      <button
        type="button"
        aria-expanded={open}
        aria-controls={contentId}
        onClick={() => setOpen((previous) => !previous)}
        className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-sm font-medium text-foreground/70 transition-colors hover:bg-hover hover:text-foreground"
      >
        <Icon className="size-4 shrink-0" aria-hidden />
        <span className="truncate">{group.label}</span>
        <ChevronRight className={cn('ml-auto size-4 transition-transform', open && 'rotate-90')} aria-hidden />
      </button>
      {open && (
        <div id={contentId} className="mt-1 space-y-1 pl-3">
          {group.children.map((child) => (
            <NavItem key={child.to} entry={child} />
          ))}
        </div>
      )}
    </div>
  );
};

const NavSectionView = ({
  section,
  collapsed,
  isFirst,
}: {
  section: NavSection;
  collapsed: boolean;
  isFirst: boolean;
}) => (
  <div className={cn(!isFirst && 'mt-3 border-t border-border pt-2')}>
    {section.label && !collapsed && (
      <div className="flex items-center justify-between px-2 pb-1">
        <span className="text-[11px] font-medium uppercase tracking-wider text-subtle">{section.label}</span>
        {section.action && (
          // `size-6` beats the icon size's `size-9` through tailwind-merge; the glyph
          // stays 16px because `buttonVariants` forces `[&_svg]:size-4`.
          <Button asChild variant="ghost" size="icon" className="size-6">
            <Link to={section.action.to} aria-label={section.action.label} title={section.action.label}>
              <Plus aria-hidden />
            </Link>
          </Button>
        )}
      </div>
    )}
    <div className="space-y-1">
      {section.entries.map((entry) =>
        entry.kind === 'leaf' ? (
          <NavItem key={entry.to} entry={entry} collapsed={collapsed} />
        ) : (
          <NavGroupRow key={entry.id} group={entry} collapsed={collapsed} />
        ),
      )}
    </div>
  </div>
);

export const AppSidebar = ({
  sections,
  officer,
  collapsed = false,
  navLabel = 'Main',
  onNavigate,
}: AppSidebarProps) => (
  <SidebarNavigateContext value={onNavigate ?? (() => undefined)}>
    <div className="flex h-full flex-col bg-card text-card-foreground">
      {/* 56px, matching UtilityBar exactly, so the brand and the utility bar share a baseline. */}
      <div className={cn('flex h-14 shrink-0 items-center px-4', collapsed && 'justify-center px-0')}>
        <NavLink to="/" onClick={onNavigate} className="flex items-center gap-2 text-sm font-semibold tracking-tight">
          {/*
           * ⚠️ THE FORCE'S OWN MARK, NOT A GENERIC GLYPH. It was `Building2` from lucide — a
           * placeholder that outlived its excuse. The file is generated from the one master by
           * `pnpm --filter @pams/pwa icons`, so this and the officer app cannot end up on two
           * different logos.
           *
           * ⚠️ `alt=""` AND `aria-hidden`: the wordmark beside it names the app, and the LINK's
           * accessible name comes from that text. An `alt` here would make the link announce
           * itself twice — and when the sidebar collapses, the wordmark goes `sr-only` rather
           * than away, so the name survives with no second source.
           */}
          <img src={kpLogo} alt="" aria-hidden className="size-5 shrink-0" width={20} height={20} />
          <span className={cn(collapsed && 'sr-only')}>PA Admin</span>
        </NavLink>
      </div>
      <Separator />

      {/*
       * ONE scroll region for the whole nav. `min-h-0` is not decoration: a flex item's
       * default `min-height: auto` refuses to shrink below its content, and with the
       * shell at `h-screen` and the aside `overflow-hidden` (which the width transition
       * needs), an over-long nav would be CLIPPED with no scrollbar anywhere.
       */}
      <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
        <nav aria-label={navLabel} className="space-y-1 p-2">
          {sections.map((section, index) => (
            <NavSectionView key={section.id} section={section} collapsed={collapsed} isFirst={index === 0} />
          ))}
        </nav>
      </div>

      {officer && <OfficerIdentity officer={officer} collapsed={collapsed} />}
    </div>
  </SidebarNavigateContext>
);
